// Media provider types. Values MUST match the backend MediaProvider ids
// (Breece.Cloud.Common.Enums.MediaProviderTypes) — the DB is authoritative and the
// media "source" enum (ENUMS.source in i18n) reuses the same numeric values, since a
// synced media file's source IS the provider type (= backend MediaProviderId) it came from.
//   0 = DynamicImages / manual upload ("Cloud Media") — not a provider, excluded from the providers UI
export enum MediaProviderTypeEnum {
  Qbank = 1,
  NewIIt = 2,
  GeneralUrl = 3,
  Perfion = 4,
  FINFO = 5,
  FTP = 6,
  SFTP = 7,
}

// Provider types offered in the "Add provider" picker, in display order.
export const MEDIA_PROVIDER_TYPES: MediaProviderTypeEnum[] = [
  MediaProviderTypeEnum.SFTP,
  MediaProviderTypeEnum.FTP,
  MediaProviderTypeEnum.FINFO,
  MediaProviderTypeEnum.Perfion,
  MediaProviderTypeEnum.Qbank,
  MediaProviderTypeEnum.NewIIt,
  MediaProviderTypeEnum.GeneralUrl,
];

// Connection shape per type. Three shapes drive which form fields show:
//   - file-transfer (SFTP/FTP): host/port/credentials/directory
//   - QBank: URL + clientId + credentials + optional template/systemName/operator
//   - everything else: a single URL
export function isFileTransferType(type: MediaProviderTypeEnum): boolean {
  return type === MediaProviderTypeEnum.SFTP || type === MediaProviderTypeEnum.FTP;
}

export function isQBankType(type: MediaProviderTypeEnum): boolean {
  return type === MediaProviderTypeEnum.Qbank;
}

export function isFtpType(type: MediaProviderTypeEnum): boolean {
  return type === MediaProviderTypeEnum.FTP;
}

// NewI.It: a URL feed that also needs credentials (username + password).
export function isNewIItType(type: MediaProviderTypeEnum): boolean {
  return type === MediaProviderTypeEnum.NewIIt;
}

// QBank search operators — the stored value is the QBank symbol/code, exactly as the legacy
// provider validates and sends them (QBank.PossibleOperators); the label is QBank's own name
// for the operator. Used with SystemName to state how to search; QBank defaults to equal.
export const QBANK_OPERATORS: { label: string; value: string }[] = [
  { label: 'equal', value: '=' },
  { label: 'not_equal', value: '!=' },
  { label: 'like', value: '~~' },
  { label: 'not-like', value: '!~~' },
  { label: 'less', value: 'lt' },
  { label: 'less_or_empty', value: 'lt0' },
  { label: 'less_or_equal', value: 'lte' },
  { label: 'less_or_equal_or_empty', value: 'lte0' },
  { label: 'greater', value: 'gt' },
  { label: 'greater_or_empty', value: 'gt0' },
  { label: 'greater_or_equal', value: 'gte' },
  { label: 'greater_or_equal_or_empty', value: 'gte0' },
  { label: 'between', value: 'BETWEEN' },
  { label: 'contains_any', value: '&&' },
  { label: 'not_contains_any', value: '!&&' },
  { label: 'contains_all', value: '@>' },
  { label: 'no_value', value: '0' },
  { label: 'has_value', value: '1' },
  { label: 'hierarchical_any', value: 'h1' },
  { label: 'hierarchical_all', value: 'h2' },
];

// FTP encryption modes (FTPS) — values match the backend/FluentFTP FtpEncryptionMode names;
// display labels live in i18n (FORM.ENCRYPTION_MODES.<mode>), ordered as in the legacy UI
// (secure-by-default first, plain FTP last). FTP only; SFTP is encrypted by protocol.
export const FTP_ENCRYPTION_MODES: string[] = ['Auto', 'Explicit', 'Implicit', 'None'];

// Simplified cache model: a provider is either "not cached" (10 min) or "cached for a day"
// (24 h). Legacy/custom minute values are still shown as-is (see cacheLabelKey), but on edit
// they collapse to one of the two options (≤ 1 h → not cached, otherwise cached).
export const CACHE_NOT_CACHED_MINUTES = 10;
export const CACHE_ONE_DAY_MINUTES = 1440;

// i18n label key for a cache value: the two simple states, else the legacy per-value label.
export function cacheLabelKey(minutes: number | null | undefined): string {
  if (minutes === CACHE_ONE_DAY_MINUTES) return 'MEDIA_PROVIDERS_LIST.FORM.CACHE_STATE.DAY';
  if (minutes == null || minutes === CACHE_NOT_CACHED_MINUTES) return 'MEDIA_PROVIDERS_LIST.FORM.CACHE_STATE.NONE';
  return 'MEDIA_PROVIDERS_LIST.FORM.CACHE.' + minutes;
}

// Per-type accent, as Tailwind classes. The colours live under `provider` in
// tailwind.config.js; these are literal class strings so Tailwind's JIT can see them
// (it scans .ts files). Hues avoid the freshness semantics (green/amber/red).
const PROVIDER_TYPE_TEXT: Record<MediaProviderTypeEnum, string> = {
  [MediaProviderTypeEnum.SFTP]: 'text-provider-sftp',
  [MediaProviderTypeEnum.FTP]: 'text-provider-ftp',
  [MediaProviderTypeEnum.FINFO]: 'text-provider-finfo',
  [MediaProviderTypeEnum.Perfion]: 'text-provider-perfion',
  [MediaProviderTypeEnum.Qbank]: 'text-provider-qbank',
  [MediaProviderTypeEnum.NewIIt]: 'text-provider-newiit',
  [MediaProviderTypeEnum.GeneralUrl]: 'text-provider-generalurl',
};

// Glyph-tile classes: coloured icon + translucent bg + border, per type.
const PROVIDER_TYPE_TILE: Record<MediaProviderTypeEnum, string> = {
  [MediaProviderTypeEnum.SFTP]: 'text-provider-sftp bg-provider-sftp/10 border-provider-sftp/30',
  [MediaProviderTypeEnum.FTP]: 'text-provider-ftp bg-provider-ftp/10 border-provider-ftp/30',
  [MediaProviderTypeEnum.FINFO]: 'text-provider-finfo bg-provider-finfo/10 border-provider-finfo/30',
  [MediaProviderTypeEnum.Perfion]: 'text-provider-perfion bg-provider-perfion/10 border-provider-perfion/30',
  [MediaProviderTypeEnum.Qbank]: 'text-provider-qbank bg-provider-qbank/10 border-provider-qbank/30',
  [MediaProviderTypeEnum.NewIIt]: 'text-provider-newiit bg-provider-newiit/10 border-provider-newiit/30',
  [MediaProviderTypeEnum.GeneralUrl]: 'text-provider-generalurl bg-provider-generalurl/10 border-provider-generalurl/30',
};

export function providerTypeText(type: MediaProviderTypeEnum): string {
  return PROVIDER_TYPE_TEXT[type] ?? PROVIDER_TYPE_TEXT[MediaProviderTypeEnum.FINFO];
}

export function providerTypeTile(type: MediaProviderTypeEnum): string {
  return PROVIDER_TYPE_TILE[type] ?? PROVIDER_TYPE_TILE[MediaProviderTypeEnum.FINFO];
}
